/**
 * 댓글 관련 Write 작업 목차
 * 
 * - createComment() - 댓글 작성
 * - createRecomment() - 답글 작성
 * - deleteComment() - 댓글 삭제
 * - deleteRecomment() - 답글 삭제
 * - toggleCommentLike() - 댓글 좋아요 토글
 * - toggleRecommentLike() - 답글 좋아요 토글
 */

import apiClient from "@/utils/api";
import { addActivityPoints } from "@/utils/write/user";

/**
 * 댓글 작성
 * @param {string} boardId - 게시글 ID
 * @param {Object} commentData - 댓글 데이터 (contents, writer, writerEmail 포함)
 * @param {File} imageFile - 댓글 이미지 파일 (선택)
 * @returns {Promise<string>} 생성된 댓글 ID
 */
export async function createComment(boardId, commentData, imageFile = null) {
  try {
    // 댓글 작성 API는 JSON으로 전송
    const data = {
      contents: commentData.contents,
      writer: commentData.writer,
      writerEmail: commentData.writerEmail,
    };

    // 이미지가 있으면 별도로 업로드
    if (imageFile) {
      const { uploadCommentImage } = await import("@/utils/write/storage");
      const imageUrl = await uploadCommentImage(imageFile, commentData.writerEmail);
      data.imageUrl = imageUrl;
    }

    const response = await apiClient.post(`/api/board/${boardId}/comment`, data);

    // 댓글 작성 점수 추가
    if (commentData.writerEmail) {
      await addActivityPoints(commentData.writerEmail, "COMMENT_WRITE");
    }

    return response.data.data.id;
  } catch (error) {
    console.error("댓글 작성 오류:", error);
    throw error;
  }
}

/**
 * 답글(대댓글) 작성
 * @param {string} boardId - 게시글 ID
 * @param {string} commentId - 댓글 ID
 * @param {Object} recommentData - 답글 데이터
 * @returns {Promise<string>} 생성된 답글 ID
 */
export async function createRecomment(boardId, commentId, recommentData) {
  try {
    const response = await apiClient.post(`/api/board/${boardId}/comment/${commentId}/recomment`, recommentData);

    // 답글 작성 점수 추가
    if (recommentData.writerEmail) {
      await addActivityPoints(recommentData.writerEmail, "RECOMMENT_WRITE");
    }

    return response.data.data.id;
  } catch (error) {
    console.error("답글 작성 오류:", error);
    throw error;
  }
}

/**
 * 댓글 삭제
 * @param {string} boardId - 게시글 ID
 * @param {string} commentId - 댓글 ID
 * @returns {Promise<void>}
 */
export async function deleteComment(boardId, commentId) {
  try {
    await apiClient.delete(`/api/board/${boardId}/comment/${commentId}`);
  } catch (error) {
    console.error("댓글 삭제 오류:", error);
    throw error;
  }
}

/**
 * 답글 삭제
 * @param {string} boardId - 게시글 ID
 * @param {string} commentId - 댓글 ID
 * @param {string} recommentId - 답글 ID
 * @returns {Promise<void>}
 */
export async function deleteRecomment(boardId, commentId, recommentId) {
  try {
    await apiClient.delete(`/api/board/${boardId}/comment/${commentId}/recomment/${recommentId}`);
  } catch (error) {
    console.error("답글 삭제 오류:", error);
    throw error;
  }
}

/**
 * 댓글 좋아요 토글
 * @param {string} boardId - 게시글 ID
 * @param {string} commentId - 댓글 ID
 * @param {string} userEmail - 사용자 이메일
 * @param {boolean} isCurrentlyLiked - 현재 좋아요 상태
 * @returns {Promise<boolean>} 새로운 좋아요 상태
 */
export async function toggleCommentLike(boardId, commentId, userEmail, isCurrentlyLiked) {
  try {
    const response = await apiClient.post(`/api/board/${boardId}/comment/${commentId}/like`, {
      userEmail,
      isLiked: isCurrentlyLiked,
    });

    const newLikedState = response.data.data.isLiked;

    // 점수 처리
    if (newLikedState) {
      await addActivityPoints(userEmail, "COMMENT_LIKE");
    } else {
      await addActivityPoints(userEmail, "COMMENT_LIKE_CANCEL");
    }

    return newLikedState;
  } catch (error) {
    console.error("댓글 좋아요 오류:", error);
    throw error;
  }
}

/**
 * 답글 좋아요 토글
 * @param {string} boardId - 게시글 ID
 * @param {string} commentId - 댓글 ID
 * @param {string} recommentId - 답글 ID
 * @param {string} userEmail - 사용자 이메일
 * @param {boolean} isCurrentlyLiked - 현재 좋아요 상태
 * @returns {Promise<boolean>} 새로운 좋아요 상태
 */
export async function toggleRecommentLike(boardId, commentId, recommentId, userEmail, isCurrentlyLiked) {
  try {
    // 답글 좋아요는 별도 엔드포인트 사용
    const response = await apiClient.post(`/api/board/${boardId}/comment/${commentId}/recomment/${recommentId}/like`, {
      userEmail,
      isLiked: isCurrentlyLiked,
    });

    const newLikedState = response.data.data.isLiked;

    // 점수 처리
    if (newLikedState) {
      await addActivityPoints(userEmail, "COMMENT_LIKE");
    } else {
      await addActivityPoints(userEmail, "COMMENT_LIKE_CANCEL");
    }

    return newLikedState;
  } catch (error) {
    console.error("답글 좋아요 오류:", error);
    throw error;
  }
}
